import { useState } from 'react';

import pick from 'lodash/pick';
import moment from 'moment';

import { AddressApi, KaseApi } from '@/api';
import { DriverLicenseApi } from '@/api/api.driver.license';
import {
  AgeGroup,
  IArrestCharge,
  IArresteeRequest,
  IMasterNameReq,
  IFile,
  IOffenseOption,
} from '@/models';
import { isValidStructuredAddress, uploadFiles } from '@/services';
import { ConfirmTypes, useToastStore } from '@/store';
import { parsePhoneNumber } from '@/utils';

import { IArrestFormData } from '../types';

interface UseArrestSubmitProps {
  kaseId: string;
  currentArrestee: any;
  filteredOffenseOptions: IOffenseOption[];
  saveNarratives?: () => Promise<void>;
  syncArrestChargesToOffenses: (arrestCharges: any[]) => Promise<void>;
  onSuccess?: () => void;
}

interface UseArrestSubmitReturn {
  isSaving: boolean;
  handleSubmit: (formData: IArrestFormData) => Promise<void>;
  onInvalidSubmit: () => void;
}

// Fields that IArresteeRequest expects - based on IArresteeForm interface
// Note: arrestLocation and offenseAddress are handled separately (need _id extraction)
const ARRESTEE_PAYLOAD_FIELDS = [
  // Basic arrest info (arrestLocation handled separately)
  'arrestDate',
  'arrestType',
  'arrestTypeCode',
  'arrestStatus',
  'arrestingOfficer',
  'bookingOfficer',
  'condition',
  'attitude',
  'medicalAttention',
  'injury',
  'drugsNarcotics',
  'bloodAlcoholLevel',
  'juvenileReleaseStatus',
  'arresteeWeapons',
  'arrestLarcenyType',
  'arresteeResidence',
  'arresteeAgeGroup',
  'arresteeAge',
  'notes',
  'arrestLocation',
  'offenseAddress',
  // files and arrestCharges handled separately

  // Administrative (NYS Arrest Report)
  'obtsNumber',
  'refNumber',
  'division',

  // Additional officers
  'assistingOfficer',
  'juvenile',
  'coDefendantsArrestNumber',

  // Miranda Rights
  'miranda',
  'mirandaBy',
  'mirandaDate',

  // Statements & Legal
  'statements',
  'courtStatus',
  'searchWarrant',
  'idProcedure',

  // Arraignment
  'arraignmentCourt',
  'arraignmentJudge',
  'arraignmentDate',

  // Evidence & Property
  'property',
  'evidence',
  'processedBy',

  // Disposition & Status
  'disposition',
  'incidentNumber',
  'bailAmount',
  'bondsman',

  // Photo & Warrant
  'photoNumber',
  'warrantNumber',

  // Other Agency
  'arrestForOtherAgency',
  'otherAgency',
  'fingerprintPictureTaken',

  // Offense Details (offenseAddress handled separately)
  'offenseDate',

  // Return Court
  'returnCourt',
  'returnJudge',
  'returnDate',

  // Transfer
  'defendantCaseTurnedOverToAgency',

  // Officer Details
  'officerName',
  'arresteeTurnedOverDate',

  // Notes & Employment
  'arrestNotes',
  'employed',

  // Signatures
  'arrestingOfficerSignature',
  'supervisorSignature',
  'safisLatentPrintIdentificationArrest',
] as const;

const MASTER_NAME_PAYLOAD_FIELDS = [
  'suffix',
  'firstName',
  'middleName',
  'lastName',
  'maidenName',
  'aliasName',
  'nickName',
  'sex',
  'birthday',
  'placeOfBirth',
  'ssn',
  'nysid',
  'fbiNumber',
  'race',
  'ethnicOrigin',
  'education',
  'religion',
  'citizenOf',
  'maritalStatus',
  'nationality',
  'gangAffiliations',
  'spokenLanguages',
  'isVerified',
  'transient',
  'driverLicense',
  'socialMedia',
  'emergencyContacts',
  'physicalCharacteristics',
  'emails',
] as const;

const normalizeEmails = (
  emailsValue: string[] | { email: string }[] | undefined,
): string[] => {
  if (!Array.isArray(emailsValue)) {
    return [];
  }

  if (emailsValue[0] && typeof emailsValue[0] === 'string') {
    return emailsValue as string[];
  }

  return (emailsValue as { email: string }[])
    .map((email) => email.email)
    .filter(Boolean);
};

const formatDriverLicenseDate = (date?: Date | string | null) => {
  if (!date) return undefined;

  const formattedDate = moment(date);
  return formattedDate.isValid()
    ? formattedDate.format('YYYY-MM-DD')
    : undefined;
};

const getDriverLicenseErrorMessage = (error: any) => {
  const validationMessage = error?.response?.data?.errors?.[0]?.msg;
  return (
    validationMessage ||
    error?.response?.data?.message ||
    error?.message ||
    'Driver license information could not be saved.'
  );
};

// Helper function to get arrestTypeCode from offense options
const getArrestTypeCode = (
  arrestType: string | undefined | null,
  offenseOptions: IOffenseOption[],
): string | undefined => {
  if (!arrestType || !offenseOptions.length) return undefined;

  const arrestTypeOption = offenseOptions.find(
    (option) => option.type === 'arrestType',
  );

  if (!arrestTypeOption) return undefined;

  const items =
    arrestTypeOption.items instanceof Map
      ? Array.from(arrestTypeOption.items.values())
      : (arrestTypeOption.items as any[]);

  const selectedItem = Array.isArray(items)
    ? items.find((item) => item.value === arrestType)
    : undefined;

  return selectedItem?.nibrsCode;
};

export const useArrestSubmit = ({
  kaseId,
  currentArrestee,
  filteredOffenseOptions,
  saveNarratives,
  syncArrestChargesToOffenses,
  onSuccess,
}: UseArrestSubmitProps): UseArrestSubmitReturn => {
  const { updateToast } = useToastStore();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Helper function to merge date and time fields
  const mergeDateAndTime = (
    date: Date | null | undefined,
    time: string | undefined,
  ): Date | null => {
    if (!date) return null;
    if (!time) return date;
    const dateObj = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    dateObj.setHours(hours || 0, minutes || 0, 0, 0);
    return dateObj;
  };

  const onInvalidSubmit = () => {
    updateToast({
      type: ConfirmTypes.WARNING,
      open: true,
      message: 'Please meet all form requirements before submitting',
    });
  };

  const handleSubmit = async (formData: IArrestFormData) => {
    if (!currentArrestee?.masterName._id?.toString()) {
      return;
    }

    // Prevent multiple submissions
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    const files: IFile[] = (currentArrestee.files || []).map((file: IFile) => ({
      ...file,
      uploadedBy: undefined,
    }));

    // Only include the fields the API expects for arrestCharges
    const arrestCharges = (currentArrestee.arrestCharges || []).map(
      (charge: IArrestCharge) => ({
        penalCode: charge.penalCode?._id?.toString(),
        attemptIndicator: charge.attemptIndicator,
        count: charge.count,
        type: charge.type,
      }),
    );

    // Extract _id from address objects (API expects string IDs, not full objects)
    const arrestLocation =
      typeof formData.arrestLocation === 'object' && formData.arrestLocation
        ? formData.arrestLocation._id?.toString()
        : formData.arrestLocation;

    const offenseAddress =
      typeof formData.offenseAddress === 'object' && formData.offenseAddress
        ? formData.offenseAddress._id?.toString()
        : formData.offenseAddress;

    // Merge time fields into date fields
    const arrestDate = mergeDateAndTime(
      formData.arrestDate,
      formData.arrestTime,
    );
    const mirandaDate = mergeDateAndTime(
      formData.mirandaDate,
      formData.mirandaTime,
    );
    const arraignmentDate = mergeDateAndTime(
      formData.arraignmentDate,
      formData.arraignmentTime,
    );
    const returnDate = mergeDateAndTime(
      formData.returnDate,
      formData.returnTime,
    );
    const arresteeTurnedOverDate = mergeDateAndTime(
      formData.arresteeTurnedOverDate,
      formData.arresteeTurnedOverTime,
    );

    // Compute arrestTypeCode at submission time to avoid race conditions
    const arrestTypeCode = getArrestTypeCode(
      formData.arrestType,
      filteredOffenseOptions,
    );

    const arresteeAge =
      formData.arresteeAgeGroup === AgeGroup.EXACT_AGE &&
      formData.arresteeAge != null
        ? Number(formData.arresteeAge)
        : null;

    const payload = {
      ...pick(formData, ARRESTEE_PAYLOAD_FIELDS),
      files,
      arrestCharges,
      arrestLocation,
      offenseAddress,
      arrestDate,
      mirandaDate,
      arraignmentDate,
      returnDate,
      arresteeTurnedOverDate,
      arrestTypeCode,
      arresteeAge,
    };

    const saveAddress = async (address: any) => {
      if (!address) return undefined;
      if (typeof address === 'string') return address;
      if (address._id) return address._id;

      const addressResponse = await AddressApi.createAddress(address);
      return addressResponse?.data?._id;
    };

    try {
      const savedAddresses = (
        await Promise.all(
          (formData.addresses || [])
            .filter((address) => isValidStructuredAddress(address))
            .map(async (address) => {
              if ((address as any)?._id) {
                return address;
              }

              const addressId = await saveAddress(address);
              return addressId ? { ...address, _id: addressId } : null;
            }),
        )
      ).filter(Boolean);

      const employerAddressId = await saveAddress(
        formData.employerInfo?.address,
      );

      const driverLicense = formData.driverLicense;
      const hasDriverLicenseData =
        driverLicense &&
        typeof driverLicense === 'object' &&
        (driverLicense.number ||
          driverLicense.customerIdNumber ||
          driverLicense.documentNumber ||
          driverLicense.state ||
          driverLicense.issueDate ||
          driverLicense.expireDate ||
          driverLicense.vehicleClass);

      let driverLicensePayload: string | undefined;
      if (hasDriverLicenseData && driverLicense.number && driverLicense.state) {
        try {
          const addressIdForLicense = savedAddresses?.[0]?._id;
          const licenseResponse = await DriverLicenseApi.saveDriverLicense({
            ...(driverLicense._id && { _id: driverLicense._id }),
            number: driverLicense.number,
            firstName: formData.firstName || '',
            middleName: formData.middleName || '',
            lastName: formData.lastName || '',
            customerIdNumber: driverLicense.customerIdNumber,
            documentNumber: driverLicense.documentNumber,
            state: driverLicense.state,
            issueDate: formatDriverLicenseDate(driverLicense.issueDate),
            expireDate: formatDriverLicenseDate(driverLicense.expireDate),
            revisionDate: formatDriverLicenseDate(driverLicense.revisionDate),
            vehicleClass: driverLicense.vehicleClass,
            restrictionCodes: driverLicense.restrictionCodes,
            endorsementCodes: driverLicense.endorsementCodes,
            documentDiscriminator: driverLicense.documentDiscriminator,
            birthday: formatDriverLicenseDate(formData.birthday),
            gender: formData.sex as any,
            height:
              formData.physicalCharacteristics?.heightExact?.feet != null
                ? Number(formData.physicalCharacteristics.heightExact.feet) *
                    12 +
                  Number(
                    formData.physicalCharacteristics.heightExact.inches || 0,
                  )
                : undefined,
            eyeColor: formData.physicalCharacteristics?.eyeColor,
            ...(addressIdForLicense && { address: addressIdForLicense }),
          });

          if (licenseResponse?.data?._id) {
            driverLicensePayload = licenseResponse.data._id;
          }
        } catch (licenseError: any) {
          throw new Error(getDriverLicenseErrorMessage(licenseError));
        }
      } else if (driverLicense?._id) {
        driverLicensePayload = driverLicense._id;
      }

      const masterNamePayload: Partial<IMasterNameReq> = {
        ...pick(formData, MASTER_NAME_PAYLOAD_FIELDS),
        refNumber: formData.masterNameRefNumber,
        addresses: savedAddresses as any,
        phoneNumbers:
          formData.phoneNumbers
            ?.filter(({ type, phoneNumber }) => type && phoneNumber)
            .map(({ type, phoneNumber }) => ({
              type,
              phoneNumber: parsePhoneNumber(phoneNumber, '+1'),
            })) ?? [],
        socialMedia:
          formData.socialMedia?.filter(
            (item) => item?.platform && item?.username,
          ) ?? [],
        emails: normalizeEmails(formData.emails),
        emergencyContacts:
          formData.emergencyContacts?.filter(
            (item) => !!item?.emergencyContact,
          ) ?? [],
        employerInfo: formData.employerInfo
          ? {
              ...formData.employerInfo,
              address: employerAddressId,
              phoneNumber: parsePhoneNumber(
                formData.employerInfo.phoneNumber,
                '+1',
              ),
            }
          : undefined,
        driverLicense: driverLicensePayload,
      };

      if (
        masterNamePayload.ssn &&
        masterNamePayload.ssn.length === 4 &&
        formData.ssn_prev
      ) {
        masterNamePayload.ssn = formData.ssn_prev;
      }

      if (Array.isArray(formData.masterNameFiles)) {
        if (formData.masterNameFiles.length) {
          const uploadedMasterNameFiles = await uploadFiles(
            formData.masterNameFiles,
          );
          if (!uploadedMasterNameFiles) return;
          masterNamePayload.files = uploadedMasterNameFiles;
        } else {
          masterNamePayload.files = [];
        }
      }

      // Upload files to tmp storage to get their tmp URLs
      if (files && files.length) {
        const uploadedFiles = await uploadFiles(files);
        if (!uploadedFiles) return;
        payload.files = uploadedFiles;
      }

      // Save arrest details FIRST
      await KaseApi.updateArrestee(
        kaseId,
        currentArrestee.masterName._id?.toString(),
        {
          ...(payload as IArresteeRequest),
          masterName: masterNamePayload,
        } as IArresteeRequest & { masterName: Partial<IMasterNameReq> },
      );

      // Sync arrest charges to offenses
      await syncArrestChargesToOffenses(arrestCharges);

      // Save narratives AFTER arrest details
      if (saveNarratives) {
        await saveNarratives();
      }

      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: 'Arrest details edited successfully',
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: err.message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isSaving,
    handleSubmit,
    onInvalidSubmit,
  };
};
